import { getI18n } from '@/plugins/i18n'
import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { useNotify } from '@/composables/notify'
import { renderMenuItem } from '@/composables/data-grid'

export const useDataGridWidget = () => {
  const i18n = getI18n()
  const cloneWidgetButton = {}
  const createProjectWidgetButton = {}
  const createInvoiceWidgetButton = {}
  const printWidgetButton = {}
  const printWidgetDropDown = {}
  const printMagicLinkWidgetButton = {}

  const cloneWidget = (options = null) => ({
    widget: 'dxButton',
    location: 'after',
    options: {...{
      icon: 'mdi mdi-file-multiple',
      text: i18n.t('common.clone'),
      disabled: true,
      onInitialized: (e) => { cloneWidgetButton.value = e.component },
    },
    ...options
    }
  })

  const createProjectWidget = (options = null) => ({
    widget: 'dxButton',
    location: 'after',
    options: {...{
      icon: 'mdi mdi-folder-plus',
      text: i18n.t('common.createProject'),
      disabled: true,
      onInitialized: (e) => { createProjectWidgetButton.value = e.component },
    },
    ...options
    }
  })

  const createInvoiceWidget = (options = null) => ({
    widget: 'dxButton',
    location: 'after',
    options: {...{
      icon: 'mdi mdi-file-document-plus-outline',
      text: i18n.t('common.generateInvoice'),
      disabled: true,
      onInitialized: (e) => { createInvoiceWidgetButton.value = e.component },
    },
    ...options
    }
  })

  const printWidget = (options = null) => ({
    widget: 'dxButton',
    location: 'after',
    options: {...{
      icon: 'mdi mdi-printer',
      text: `${i18n.t('common.print')}...`,
      disabled: true,
      onInitialized: (e) => { printWidgetButton.value = e.component },
    },
    ...options
    }
  })

  const printDropDownWidget = (options = null) => ({
    widget: 'dxDropDownButton',
    location: 'after',
    options: {...{
      icon: 'mdi mdi-printer',
      text: `${i18n.t('common.print')}...`,
      displayExpr: 'name',
      keyExpr: 'key',
      useSelectMode: false,
      disabled: true,
      items: options?.items || [],
      dropDownOptions: { width: 'auto', minWidth: '14rem' },
      onInitialized: (e) => { printWidgetDropDown.value = e.component },
    },
    ...options
    }
  })

  const actionWidget = (button, options) => ({
    widget: 'dxButton',
    location: 'after',
    options: {
      disabled: true,
      onInitialized: (e) => { button.value = e.component },
      ...options,
    },
  })

  const actionsMenuWidget = (menu, options) => ({
    widget: 'dxDropDownButton',
    location: 'after',
    options: {
      icon: 'mdi mdi-lightning-bolt-outline',
      text: i18n.t('common.action', 2),
      useSelectMode: false,
      itemTemplate: renderMenuItem,
      dropDownOptions: { width: 'auto', minWidth: '16rem' },
      onItemClick: (e) => e.itemData.onClick(),
      onInitialized: (e) => { menu.value = e.component },
      ...options,
    },
  })

  const printMagicLinkWidget = (options = null) => ({
    widget: 'dxButton',
    location: 'after',
    options: {...{
      icon: 'mdi mdi-link-variant',
      text: i18n.t('common.getMagicLink'),
      disabled: true,
      onInitialized: (e) => { printMagicLinkWidgetButton.value = e.component },
    },
    ...options
    }
  })

  return {
    actionWidget,
    actionsMenuWidget,
    cloneWidget,
    createProjectWidget,
    createInvoiceWidget,
    printWidget,
    printDropDownWidget,
    printMagicLinkWidget,
    cloneWidgetButton,
    createProjectWidgetButton,
    createInvoiceWidgetButton,
    printWidgetButton,
    printWidgetDropDown,
    printMagicLinkWidgetButton
  }
}

export const useProjectPresentationExport = (label) => {
  const i18n = getI18n()
  const accountingStore = useAccountingStore()
  const planningStore = usePlanningStore()
  const { notifyWarning } = useNotify()

  return {
    label,
    isDisabled: (selectedRowsData) => selectedRowsData.length !== 1,
    onClick: async([selectedRow]) => {
      const templates = accountingStore.getLocalizedBudgetDocumentExportTemplates()
      if (!templates.length) return
      if (!selectedRow.budget_document) {
        notifyWarning(i18n.t('common.exportExcelNoBudgetDocument'), {timeout: 0, actions: [{icon: 'mdi-close', color: 'dark', padding: 'xs', size: 'sm', round: true, flat: true}]})
        return
      }
      await planningStore.exportProjectsExcelAsync([selectedRow.id], templates[0].id, `${selectedRow.name} - ${label}.xlsx`)
    },
  }
}
